import { cn } from '@/lib/utils';
import { BlueprintMarkdown } from '@/components/BlueprintMarkdown';
import { CollapsibleSection } from '@/components/CollapsibleSection';
import type { AnalysisResults, Blueprint, NucliusScorecardSections } from '@/types/analysis';
import MandateOverviewPanel from '@/components/scorecard/MandateOverview';
import {
  RecommendationHeadlineMetrics,
  stripLegacyRecommendationMetrics,
} from '@/components/scorecard/RecommendationHeadlineMetrics';
import StoryOverviewSection, {
  type StoryOverviewStructured,
} from '@/components/scorecard/StoryOverviewSection';
import EngagementSection, {
  type EngagementStructured,
} from '@/components/scorecard/EngagementSection';
import CommercialitySection, {
  type CommercialityStructured,
} from '@/components/scorecard/CommercialitySection';
import MandateSection, { type MandateStructured } from '@/components/scorecard/MandateSection';

const SECTION_NAV: { key: keyof NucliusScorecardSections; label: string }[] = [
  { key: 'story_overview', label: 'Story Overview' },
  { key: 'recommendation', label: 'Recommendation' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'commerciality', label: 'Commerciality' },
  { key: 'mandate', label: 'Mandate' },
];

type NucliusScorecardReportProps = {
  results: AnalysisResults;
  blueprint: Blueprint;
  sections: NucliusScorecardSections;
};

const RECOMMENDATION_COLORS: Record<string, string> = {
  Greenlight: 'bg-emerald-600/20 text-emerald-300 border-emerald-500/40',
  Develop: 'bg-sky-600/20 text-sky-300 border-sky-500/40',
  Consider: 'bg-amber-600/20 text-amber-200 border-amber-500/40',
  Hold: 'bg-orange-600/20 text-orange-200 border-orange-500/40',
  Pass: 'bg-red-600/20 text-red-300 border-red-500/40',
};

export function NucliusScorecardReport({ results, blueprint, sections }: NucliusScorecardReportProps) {
  const structured = blueprint.sections_structured?.recommendation;
  const storyStructured = blueprint.sections_structured?.story_overview;
  const engagementStructured = blueprint.sections_structured?.engagement;
  const commercialityStructured = blueprint.sections_structured?.commerciality;
  const mandateStructured = blueprint.sections_structured?.mandate;
  const label = structured?.recommendation_label ?? blueprint.commission_summary ?? '—';
  const badgeClass = RECOMMENDATION_COLORS[label] ?? 'bg-white/10 text-white border-white/20';
  const recommendationMarkdown = stripLegacyRecommendationMetrics(sections.recommendation ?? '');

  return (
    <div className="min-w-0 w-full space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="ni-label shrink-0">Nuclius Index report</span>
        <span
          className={cn(
            'inline-flex shrink-0 items-center rounded-md border px-3 py-1 text-sm font-semibold',
            badgeClass
          )}
        >
          {label}
        </span>
      </div>

      <nav className="flex flex-wrap gap-2 text-xs min-w-0 w-full">
        {SECTION_NAV.map(({ key, label: navLabel }) =>
          sections[key]?.trim() ? (
            <a
              key={key}
              href={`#nuclius-${key}`}
              className="shrink-0 rounded-md border border-white/10 px-3 py-1.5 text-white/70 hover:bg-white/5 hover:text-white whitespace-nowrap"
            >
              {navLabel}
            </a>
          ) : null
        )}
      </nav>

      {sections.recommendation?.trim() && (
        <div id="nuclius-recommendation">
          <CollapsibleSection title="Recommendation" defaultOpen variant="gold">
            <RecommendationHeadlineMetrics
              engagementPct={structured?.engagement_pct ?? results.scorecard?.engagement_percentage}
              commercialityPct={
                structured?.commerciality_pct ??
                results.scorecard?.v7_scores?.commercial_viability_pct ??
                results.scorecard?.commercial_viability_pct
              }
              mandateScore={
                structured?.mandate_score ?? results.mandate_overview?.match_pct ?? null
              }
              recommendationLabel={label !== '—' ? label : structured?.recommendation_label}
            />
            <div className="[&_.blueprint-markdown]:text-white/85">
              <BlueprintMarkdown markdown={recommendationMarkdown} />
            </div>
          </CollapsibleSection>
        </div>
      )}

      {sections.story_overview?.trim() && (
        <div id="nuclius-story_overview">
          <CollapsibleSection title="Story Overview" defaultOpen>
            <StoryOverviewSection
              markdown={sections.story_overview}
              structured={storyStructured as StoryOverviewStructured | undefined}
            />
          </CollapsibleSection>
        </div>
      )}

      {sections.engagement?.trim() && (
        <div id="nuclius-engagement">
          <CollapsibleSection title="Engagement">
            <EngagementSection
              markdown={sections.engagement}
              structured={engagementStructured as EngagementStructured | undefined}
            />
            {!((engagementStructured as EngagementStructured | undefined)?.metrics?.length) ? (
              <div className="[&_.blueprint-markdown]:text-white/85">
                <BlueprintMarkdown markdown={sections.engagement} />
              </div>
            ) : null}
          </CollapsibleSection>
        </div>
      )}

      {sections.commerciality?.trim() && (
        <div id="nuclius-commerciality">
          <CollapsibleSection title="Commerciality">
            <CommercialitySection
              structured={commercialityStructured as CommercialityStructured | undefined}
            />
            {!(commercialityStructured as CommercialityStructured | undefined)?.originality ? (
              <div className="[&_.blueprint-markdown]:text-white/85">
                <BlueprintMarkdown markdown={sections.commerciality} />
              </div>
            ) : null}
          </CollapsibleSection>
        </div>
      )}

      {sections.mandate?.trim() && (
        <div id="nuclius-mandate">
          <CollapsibleSection title="Mandate">
            {results.mandate_overview ? (
              <MandateOverviewPanel overview={results.mandate_overview} embedded />
            ) : null}
            <MandateSection structured={mandateStructured as MandateStructured | undefined} />
            {!(mandateStructured as MandateStructured | undefined)?.overall_feedback ? (
              <div className="mt-4 [&_.blueprint-markdown]:text-white/85">
                <BlueprintMarkdown markdown={sections.mandate} />
              </div>
            ) : null}
          </CollapsibleSection>
        </div>
      )}

      {sections.full?.trim() && (
        <CollapsibleSection title="Full analysis output (appendix)" description="Complete agent outputs.">
          <div className="[&_.blueprint-markdown]:text-white/75">
            <BlueprintMarkdown markdown={sections.full} />
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

export default NucliusScorecardReport;
